<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useYardAssetPresenterV2 } from '@satellite/composables/useYardAssetPresenterV2';
import YardSpotPresenter from './YardSpotPresenter.vue';

import type { Maybe } from '@nova/core';
import type { YardAsset } from '@satellite/composables/useYardAssetPresenterV2';

const props = withDefaults(
  defineProps<{
    size?: 'regular' | 'small';
    variant?: 'default' | 'asset-only' | 'spot-only';
    asset?: Maybe<YardAsset>;
    assetId?: Maybe<string>;
  }>(),
  {
    size: 'regular',
    variant: 'default',
    asset: null,
    assetId: null
  }
);

const { t } = useI18n();

const { assetId, asset } = toRefs(props);

const { assetCode, assetType, spotId, isLoading, onIntersect } = useYardAssetPresenterV2({
  assetId,
  asset,
  live: true
});

const canShowAssetInfo = computed(() => {
  return assetCode.value != null && assetType.value != null;
});

const hasAssignment = computed(() => {
  return Boolean(spotId.value);
});

const getIconByAssetStatus = () => {
  if (hasAssignment.value) {
    return 'mdi-map-marker-outline';
  }
  return 'mdi-clock-outline';
};

const getIconSize = () => {
  if (props.size === 'small') {
    return '12';
  }
  return '16';
};

const showAsset = computed(() => {
  return ['default', 'asset-only'].includes(props.variant);
});

const showSpot = computed(() => {
  return ['default', 'spot-only'].includes(props.variant);
});
</script>

<template>
  <div
    class="d-flex flex-column align-start gap-1"
    style="min-width: 50px"
    v-intersect="{
      handler: onIntersect,
      options: {
        threshold: [0.5]
      }
    }">
    <span v-if="canShowAssetInfo && showAsset" class="d-flex flex-row gap-1 align-center">
      <span
        class="typography-body-sm-bold text-capitalize"
        data-testid="yard-asset-presenter-asset-code">
        {{ assetCode }}
      </span>
      <v-chip
        variant="tonal"
        size="x-small"
        class="typography-chips-sm text-capitalize"
        data-testid="yard-asset-presenter-asset-type">
        {{ assetType }}
      </v-chip>
    </span>

    <span v-if="showSpot" class="d-flex flex-row gap-1 align-center">
      <v-icon :icon="getIconByAssetStatus()" :size="getIconSize()" />
      <v-skeleton-loader v-if="isLoading" type="text" style="min-width: 50px" />
      <yard-spot-presenter
        v-else-if="hasAssignment"
        :spot-id="spotId"
        :size="size"
        style="min-width: 50px" />
      <template v-else>
        <span
          class="text-muted"
          data-testid="yard-asset-presenter-waiting-assignment"
          :class="size === 'small' ? 'typography-body-sm' : 'typography-body-md'">
          {{ t('yard.drawers.createTaskDrawer.labels.waitingAssignment') }}
        </span>
      </template>
    </span>
  </div>
</template>

<style scoped>
:deep(.v-skeleton-loader__text) {
  /** this value makes the loader have the same vertical spacing as the asset code text */
  margin: 6px 0 !important;
}
</style>
